import { useEffect, useState } from 'react';

import {
  Avatar,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import { useModal } from 'react-modal-hook';

import { BlockedContactBadge } from 'components/BlockedContactBadge';
import { BlockedContactName } from 'components/BlockedContactName';
import ImageViewer from 'components/ImageViewer';
import InfiniteScroll from 'components/InfiniteScroll';
import ModalAddContato from 'components/ModalAddContato';
import ModalIniciarContato from 'components/ModalIniciarContato';
import SearchBar from 'components/SearchBar';
import { useAppDispatch, useAppSelector } from 'store';
import { ContatoInterface } from 'store/reducers/contatos';
import {
  fetchContatosByGrupo,
  gruposSelector,
  resetContatosGrupo,
} from 'store/reducers/grupos';

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    height: '100%',
  },
  list: {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: spacing(1),
  },
}));

export default function TabPanelListaMembros() {
  const classes = useStyles();
  const [filter, setFilter] = useState('');
  const [contatoSelected, setContatoSelected] = useState<
    ContatoInterface | undefined
  >();
  const dispatch = useAppDispatch();
  const {
    grupoSelecionado,
    contatosGrupo,
    contatosGrupoCursor,
    loadingContatosGrupo,
  } = useAppSelector(gruposSelector);

  const handleSearch = (value: string) => {
    const grupoChatId = grupoSelecionado?.id;
    if (grupoChatId) {
      setFilter(value);
      dispatch(resetContatosGrupo());
      dispatch(fetchContatosByGrupo({ grupoChatId, filter: value }));
    }
  };

  const handleCancelSearch = () => {
    const grupoChatId = grupoSelecionado?.id;
    if (grupoChatId) {
      setFilter('');
      dispatch(resetContatosGrupo());
      dispatch(fetchContatosByGrupo({ grupoChatId }));
    }
  };

  const onLoadNextContatos = () => {
    const grupoChatId = grupoSelecionado?.id;
    const { afterCursor } = contatosGrupoCursor;

    if (grupoChatId && afterCursor) {
      dispatch(fetchContatosByGrupo({ grupoChatId, cursor: { afterCursor } }));
    }
  };

  const handleClickAddContato = (contato: ContatoInterface) => {
    setContatoSelected(contato);
    showModalAddContato();
  };

  const handleClickIniciarContato = (contato: ContatoInterface) => {
    setContatoSelected(contato);
    showModalIniciarContato();
  };

  const [showModalAddContato, hideModalAddContato] = useModal(
    ({ in: open }) => (
      <ModalAddContato
        open={open}
        contato={contatoSelected}
        onClose={hideModalAddContato}
        onSuccess={handleCancelSearch}
      />
    ),
    [contatoSelected],
  );

  const [showModalIniciarContato, hideModalIniciarContato] = useModal(
    ({ in: open }) => (
      <ModalIniciarContato
        open={open}
        contato={contatoSelected}
        onClose={hideModalIniciarContato}
      />
    ),
    [contatoSelected],
  );

  useEffect(() => {
    const grupoChatId = grupoSelecionado?.id;

    if (grupoChatId) dispatch(fetchContatosByGrupo({ grupoChatId }));
  }, [dispatch, grupoSelecionado]);

  const renderMembros = contatosGrupo.map(contato => {
    const isBlocked = Boolean(contato.blockedAt || contato.bloqueadoEm);

    return (
      <ListItem key={contato.id} disableGutters dense>
        <ListItemAvatar>
          <ImageViewer src={contato.foto}>
            <BlockedContactBadge isBlocked={isBlocked}>
              <Avatar src={contato.foto} />
            </BlockedContactBadge>
          </ImageViewer>
        </ListItemAvatar>

        <ListItemText
          primary={
            <BlockedContactName
              name={contato.nome}
              isBlocked={isBlocked}
              variant="body2"
            />
          }
          secondary={contato.contato}
          primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
          secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
        />

        <ListItemSecondaryAction>
          {contato.tipo === 'ANONIMO' && (
            <Tooltip placement="top" title="Adicionar aos contatos">
              <IconButton
                onClick={() => handleClickAddContato(contato)}
                edge="end"
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip
            placement="top"
            title={
              isBlocked
                ? 'Contato bloqueado. Não é possível iniciar atendimento.'
                : 'Iniciar contato'
            }
          >
            <span>
              <IconButton
                onClick={() => handleClickIniciarContato(contato)}
                edge="end"
                disabled={isBlocked}
              >
                <PlayArrowIcon />
              </IconButton>
            </span>
          </Tooltip>
        </ListItemSecondaryAction>
      </ListItem>
    );
  });

  return (
    <Box pl={2} pr={2} className={classes.root}>
      <Box mb={1}>
        <SearchBar
          value={filter}
          onChange={handleSearch}
          onCancelSearch={handleCancelSearch}
        />
      </Box>

      <List id="listContatosGrupo" className={classes.list}>
        <InfiniteScroll
          data={contatosGrupo}
          next={onLoadNextContatos}
          hasMore={Boolean(contatosGrupoCursor.afterCursor)}
          loading={loadingContatosGrupo}
          scrollableTarget="listContatosGrupo"
          empty={{
            title: filter
              ? 'Tem certeza que digitou certo?!'
              : 'O grupo está vazio',
            description: filter
              ? `Não conseguimos encontrar ninguém chamado "${filter}"`
              : 'Este grupo não possui nenhum participante',
          }}
        >
          {renderMembros}
        </InfiniteScroll>
      </List>
    </Box>
  );
}
